/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Token Queue - Request queue for limiting concurrent token fetches
 */

import { MAX_CONCURRENT_TOKEN_REQUESTS } from "./token-constants";
import { updateTokenLoadingState } from "./token-state";
import type { QueuedRequest } from "./token-types";

// ============================================================================
// REQUEST QUEUE
// ============================================================================

/**
 * Queue for pending token requests
 */
export const tokenRequestQueue: QueuedRequest[] = [];

/**
 * Counter for active token requests
 */
export let activeTokenRequests = 0;

/**
 * Increment active requests counter
 */
export function incrementActiveRequests(): void {
  activeTokenRequests++;
}

/**
 * Decrement active requests counter
 */
export function decrementActiveRequests(): void {
  activeTokenRequests--;
}

/**
 * Reset active requests counter (used by clearTokenCache)
 */
export function resetActiveRequests(): void {
  activeTokenRequests = 0;
  tokenRequestQueue.length = 0;
}

/**
 * Check if we have capacity for more requests
 */
export function hasCapacity(): boolean {
  return activeTokenRequests < MAX_CONCURRENT_TOKEN_REQUESTS;
}

/**
 * Check if we're at capacity
 */
export function isAtCapacity(): boolean {
  return activeTokenRequests >= MAX_CONCURRENT_TOKEN_REQUESTS;
}

// ============================================================================
// QUEUE PROCESSING
// ============================================================================

/**
 * Type for the token fetch function (to avoid circular dependency)
 */
type FetchTokenFn = (
  getToken: QueuedRequest["getToken"],
  cacheKey: string,
) => Promise<string | null>;

/**
 * Process token request queue when capacity is available
 * @param fetchTokenWithRetry - The fetch function to use for processing
 */
export function processTokenQueue(fetchTokenWithRetry: FetchTokenFn): void {
  // Process queue if we have capacity and queued requests
  while (hasCapacity() && tokenRequestQueue.length > 0) {
    const queued = tokenRequestQueue.shift();
    if (!queued) break;

    incrementActiveRequests();
    updateTokenLoadingState({
      isLoading: true,
      activeRequests: activeTokenRequests,
      queuedRequests: tokenRequestQueue.length,
    });

    // Process the queued request
    (async () => {
      try {
        const token = await fetchTokenWithRetry(
          queued.getToken,
          queued.cacheKey,
        );
        queued.resolve(token);
      } catch (error) {
        queued.reject(
          error instanceof Error ? error : new Error("Token fetch failed"),
        );
      } finally {
        decrementActiveRequests();
        updateTokenLoadingState({
          activeRequests: activeTokenRequests,
          queuedRequests: tokenRequestQueue.length,
          isLoading: activeTokenRequests > 0 || tokenRequestQueue.length > 0,
        });
        // Process next item in queue (recursive)
        processTokenQueue(fetchTokenWithRetry);
      }
    })();
  }
}

/**
 * Add request to queue
 */
export function enqueueRequest(request: QueuedRequest): void {
  tokenRequestQueue.push(request);
  updateTokenLoadingState({
    queuedRequests: tokenRequestQueue.length,
    isLoading: true,
  });
}
